import { ActionIcon, Box, Group, Stack } from '@mantine/core';
import { InitiativeStatus } from '../../../../../api/initiative-client/initiative-client.type';
import type { Milestone } from '../../../../../api/milestones-client/milestones-client.type';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../../helpers/date-time-helpers/formatting/formatting';
import { useMilestonesTabStore } from '../../../../../store/strategy-store/strategy-store';
import { setSelectedMilestone } from '../../../../../store/strategy-store/strategy-store.actions';
import { inkLight, primaryBase, secondaryLight, skyDark, skyLightest } from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../../ui-library/typography/typography';
import { getStatusColor } from '../../../components/strategy-entity-table.helpers';

type Props = {
  milestone: Milestone;
  setMilestoneModalMode: (mode: 'create' | 'edit') => void;
  setIsMilestoneModalOpen: (open: boolean) => void;
  isLast?: boolean;
};

const ProgressBar = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <Box style={{ flex: 1 }}>
    <Group justify="space-between" align="center" gap={4}>
      <Text style={{ fontSize: '0.75rem', color: '#6c7072' }}>{label}</Text>
      <Text style={{ fontSize: '0.75rem', fontWeight: 500 }}>{Math.round(value)}%</Text>
    </Group>
    <Box
      style={{
        height: 4,
        backgroundColor: '#e3e5e5',
        borderRadius: 2,
        overflow: 'hidden',
        marginTop: 2,
      }}
    >
      <Box
        style={{
          height: '100%',
          width: `${value}%`,
          backgroundColor: color,
          borderRadius: 2,
        }}
      />
    </Box>
  </Box>
);

export const MilestoneItem = ({ milestone, setMilestoneModalMode, setIsMilestoneModalOpen }: Props) => {
  const i18n = useTranslation();
  const selectedMilestone = useMilestonesTabStore((state) => state.selectedMilestone);
  const isSelected = milestone.id === selectedMilestone?.id;

  return (
    <Box
      onClick={() => setSelectedMilestone(milestone)}
      style={{
        boxSizing: 'border-box',
        borderRadius: 8,
        padding: '12px 16px',
        border: `1px solid ${isSelected ? secondaryLight : skyDark}`,
        backgroundColor: isSelected ? skyLightest : 'transparent',
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <Stack gap={8}>
        <Group justify="space-between" align="flex-start">
          <Tooltip variant="black" label={i18n.t(`strategy.initiatives.statuses.${milestone.status}`)}>
            <Group gap="xs">
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 8,
                  backgroundColor: getStatusColor(milestone.status || InitiativeStatus.Unknown),
                }}
              />

              <Text size="small" weight="medium">
                {milestone.name}
              </Text>
            </Group>
          </Tooltip>

          {isSelected && (
            <ActionIcon
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation();
                setMilestoneModalMode('edit');
                setIsMilestoneModalOpen(true);
              }}
              size="sm"
            >
              <Icon name="edit" size={14} style={{ cursor: 'pointer' }} />
            </ActionIcon>
          )}
        </Group>

        <Group justify="space-between" align="center">
          <Text size="tiny" color={inkLight}>
            Due Date: {formatDate(milestone.target_end_date)}
          </Text>
          <Text size="tiny" color={inkLight}>
            {milestone.epics.length}{' '}
            {milestone.epics.length === 1 ? i18n.t('common.parent_task') : `${i18n.t('common.parent_task')}s`}
          </Text>
        </Group>

        <Stack gap={8}>
          <ProgressBar label="Progress" value={milestone.percent_progress} color={primaryBase} />
        </Stack>
      </Stack>
    </Box>
  );
};
